/** `rule.takeTurns` — one at a time, forever: every tagged entity a level
 * places marks a SLOT (it waits hidden). One slot's item is shown at a time;
 * when it is gone (collected) the next appears after a gap at another slot
 * (seeded random, or slot order), re-spawned there if its item was taken —
 * the arcade power item that reappears elsewhere after each stun. A reset
 * signal (a new phase's layout) forgets the slots. */
import type { JsonObject } from '../../json.js';
import { id, int, list, obj, oneOf, readsSignal } from '../schemaBuilders.js';
import { defineModule, num as read, str, type ModuleCtx } from '../module.js';

export type TakeTurnsConfig = { tag: string; firstDelayTicks: number; gapTicks: number; order: 'random' | 'cycle'; resetOn: string[] };

type Slot = { archetype: string; x: number; y: number; id: string | null };

function reset(ctx: ModuleCtx<TakeTurnsConfig>): void {
  Object.assign(ctx.state, { slots: [], current: null, last: -1, timer: ctx.config.firstDelayTicks });
}

/** A tagged item placed since the last pass waits hidden and becomes a slot. */
function adopt(ctx: ModuleCtx<TakeTurnsConfig>): void {
  const s = ctx.state, slots = s.slots as Slot[];
  for (const e of ctx.entities({ tag: ctx.config.tag, status: 'any' })) {
    if (e.id === s.current || slots.some(slot => slot.id === e.id)) continue;
    slots.push({ archetype: e.archetype, x: e.x, y: e.y, id: e.id });
    e.status = 'hidden';
  }
}

export const takeTurns = defineModule<TakeTurnsConfig>({
  id: 'rule.takeTurns', version: 1, attach: 'global', role: 'rule',
  summary: 'Tagged items take turns at their placed slots: one shown at a time; after it is taken the next appears elsewhere after a gap, forever.',
  config: obj({ tag: id(), firstDelayTicks: int(0, 3600), gapTicks: int(0, 3600), order: oneOf('random', 'cycle'), resetOn: list(readsSignal(), 4) }),
  requires: [], events: ['appear'], presentation: { appear: { cue: 'none', vfx: { effect: 'burst', palette: 'slot', count: 6 } } },
  proofs: ['only-one-shown', 'next-after-gap-at-another-slot', 'taken-slot-respawns', 'first-delay', 'reset-forgets-slots'],
  animStates: () => [],
  initGlobal: reset,
  systems: {
    flow(ctx) {
      const c = ctx.config, s = ctx.state;
      if (c.resetOn.some(kind => ctx.signals(kind).length)) reset(ctx);
      const current = ctx.entity(str(s, 'current'));
      if (current && current.status !== 'hidden') return;
      if (s.current !== null) { s.current = null; s.timer = c.gapTicks; }
      const slots = s.slots as Slot[];
      if (!slots.length) return;
      if (read(s, 'timer') > 0) { s.timer = read(s, 'timer') - 1; return; }
      // Another slot than the last one, when there is a choice.
      const options = slots.map((_, i) => i).filter(i => slots.length === 1 || i !== read(s, 'last'));
      const index = c.order === 'random' ? options[ctx.randomInt(options.length)]! : options.find(i => i > read(s, 'last')) ?? options[0]!;
      const slot = slots[index]!;
      let item = ctx.entity(slot.id);
      if (!item) {
        item = ctx.spawn(slot.archetype, { x: slot.x, y: slot.y });
        slot.id = item.id;
      }
      item.status = 'active';
      Object.assign(s, { current: item.id, last: index });
      ctx.emit('appear', { x: item.x, y: item.y - item.h / 2 });
    },
    // Input (first stage): items the level placed at boot are hidden before
    // anything can touch them on the first tick.
    input: adopt,
    // Present (last stage): an item placed this tick waits hidden before a
    // snapshot can show it.
    present: adopt,
  },
});
